


import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import MenuCategories from './MenuCategories/MenuCategories';
import styles from './Menu.module.less';
import { Icon } from '../../shared/Icon/Icon';
import { useFilters } from '../../../hooks/useFilters';
import { useMenuStore, useMenuStateStore } from '../../../stores/menuStore';
import { useThemeStore } from '../../../stores/themeStore';
import { useCategories } from '../../../hooks/useCategories';
import MenuHeader from './HeaderMenu/HeaderMenu';
import useIsMobile from '../../../hooks/useIsMobile';
import ThemeToggle from '../../SwitcherBlock/ThemeToggle/ThemeToggle';
import LanguageSwitcher from '../../SwitcherBlock/LanguageSwitcher/LanguageSwitcher';
import { useSearchStore } from '../../../stores/searchStore';
import { useDropdownStore } from '../../../stores/dropdownStore';

const Menu = () => {
  const navigate = useNavigate();
  const { isMenuOpen, closeMenu, openMenu } = useMenuStateStore();
  const { closeSearch} = useSearchStore();
  const {
    tempSelectedCategory,
    tempSelectedSubcategories,
    resetTempSelections,
    currentStep,
    setCurrentStep,
    setTempCategory,
    setTempSubcategories,
  } = useMenuStore();

  const {
    selectedCategory,
    selectedSubcategories,
    setSelectedCategory,
    setSelectedSubcategories,
  } = useFilters();

  const { theme } = useThemeStore();
  const { data: categoriesData } = useCategories();
  const isMobile = useIsMobile();
  const { isOpen } = useDropdownStore();
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (isMenuOpen) {
      if (selectedCategory) {
        setCurrentStep('subcategories');
        setTempCategory(selectedCategory);
        setTempSubcategories(selectedSubcategories);
      } else {
        setCurrentStep('categories');
        setTempCategory(null);
        setTempSubcategories([]);
      }
    }
  }, [
    isMenuOpen,
    selectedCategory,
    selectedSubcategories,
    setTempCategory,
    setTempSubcategories,
    setCurrentStep,
  ]);

  useEffect(() => {
    if (!isMenuOpen) return;
    
    function handleClickOutside(event: MouseEvent) {
      if (
        menuRef.current && !menuRef.current.contains(event.target as Node) &&
        menuButtonRef.current && !menuButtonRef.current.contains(event.target as Node)
      ) {
        closeMenu();
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen, closeMenu]);

  const handleApplyFilters = () => {
    setSelectedCategory(tempSelectedCategory);
    setSelectedSubcategories(tempSelectedSubcategories);
    navigate('/');
    closeMenu();
  };

  const handleIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isMenuOpen) {
      closeMenu();
    } else {
      openMenu();
      closeSearch();
    }
  };

  const handleClose = () => {
    resetTempSelections();
    closeMenu();
  };

  const handleBack = () => {
    setCurrentStep('categories');
    setTempCategory(null);
    setTempSubcategories([]);
  };

  const selectedCategoryData = categoriesData?.categories.find(
    (cat) => cat.id === tempSelectedCategory
  );


  const isDisabled = isOpen;

  

  return (
    <>
      <button
        ref={menuButtonRef}
        className={styles.menu__button}
        onClick={handleIconClick}
        disabled={isDisabled}
      >
        <Icon
          id="menu"
          className={`${styles.icon} ${theme !== 'light' && styles.iconLight}`}
        />
      </button>

      {isMenuOpen && (
        <div className={styles.menu__dropdown} ref={menuRef}>
          <div>
            <MenuHeader
              onClose={handleClose}
              onApplyFilters={handleApplyFilters}
              selectedCategory={selectedCategoryData}
              onBack={handleBack}
              showBackButton={currentStep === 'subcategories'}
              showApplyButton={true}
            />

            <MenuCategories />
          </div>

          {isMobile && (
            <div className={styles.menu__dropdown__switchers}>
              <ThemeToggle themes="dark" />
              <LanguageSwitcher theme="dark" isMenu={true} />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Menu;
